import React, { useState, useRef, useEffect } from 'react';
import MentionSuggestions from './MentionSuggestions';
import { 
  getMentionContext, 
  insertMention, 
  parseMentions, 
  validateMentions,
  notifyMentionedUsers
} from '../services/mentionService';
import { useAuth } from '../context/AuthContext';
import { validateInput, contentModeration, rateLimiter } from '../utils/security';
import ProfileRing from './ProfileRing';
import { getUserActiveRing } from '../services/profileRingService';
import { getUserProfile } from '../services/questionsService';

function CommentFormWithMentions({ 
  onSubmit, 
  parentId = null, 
  onCancel = null, 
  userVoteOption = null,
  questionId = null,
  contextType = 'comment'
}) {
  // If userVoteOption is null, provide a default value
  const voteColor = userVoteOption === 'A' ? 'white' : userVoteOption === 'B' ? 'black' : '#ddd';
  const { currentUser } = useAuth();
  const [text, setText] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [showMentions, setShowMentions] = useState(false);
  const [mentionContext, setMentionContext] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUserRing, setCurrentUserRing] = useState(null);
  const [userPhotoURL, setUserPhotoURL] = useState(null);
  const textareaRef = useRef(null);
  
  // Load current user's profile ring and photo
  useEffect(() => {
    const loadUserData = async () => {
      if (currentUser?.uid) {
        try {
          // Load ring
          const ringId = await getUserActiveRing(currentUser.uid);
          setCurrentUserRing(ringId);
          
          // Load user profile for photo
          const profile = await getUserProfile(currentUser.uid);
          if (profile && profile.photoURL) {
            setUserPhotoURL(profile.photoURL);
          } else {
            setUserPhotoURL(currentUser.photoURL);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          setCurrentUserRing(null);
          setUserPhotoURL(currentUser.photoURL);
        }
      }
    };
    
    loadUserData();
  }, [currentUser?.uid]);

  // Handle input change and detect @mentions
  const handleInputChange = (e) => {
    const { value, selectionStart } = e.target;
    setText(value);
    
    // Check for mention context using enhanced service
    const context = getMentionContext(value, selectionStart);
    
    if (context && context.isActive) {
      setMentionContext(context);
      setMentionQuery(context.searchTerm);
      setShowMentions(true);
      
      // Calculate position for suggestion popup
      calculateMentionPosition(context.startIndex);
    } else {
      setShowMentions(false);
      setMentionContext(null);
      setMentionQuery('');
    }
  };
  
  // Calculate position for mention suggestions (simplified approach)
  const calculateMentionPosition = (atIndex) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const rect = textarea.getBoundingClientRect();
    
    // Simple positioning: below the textarea
    const position = {
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX
    };
    
    setMentionPosition(position);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!text.trim() || isSubmitting) return;
    
    // Rate limiting check
    const rateCheck = rateLimiter.isRateLimited('comment', currentUser?.uid || 'anonymous', 10, 60000);
    if (rateCheck.limited) {
      alert('Too many comments posted. Please wait a moment before commenting again.');
      return;
    }
    
    // Validate and sanitize input
    const validation = validateInput.comment(text);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }
    
    // Content moderation check
    const moderation = contentModeration.checkContent(validation.value);
    if (!moderation.safe) {
      alert('Your comment contains inappropriate content. Please revise and try again.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Parse and validate mentions
      const mentions = parseMentions(validation.value);
      const validMentions = await validateMentions(mentions);
      
      // Submit the comment with sanitized text
      const result = await onSubmit(validation.value, parentId);
      
      // Send notifications to mentioned users
      if (validMentions.length > 0 && currentUser && result) {
        await notifyMentionedUsers(
          validMentions,
          {
            uid: currentUser.uid,
            displayName: currentUser.displayName,
            username: currentUser.username || currentUser.displayName || 'user'
          },
          {
            type: contextType,
            id: result.id || parentId || questionId,
            text: text.slice(0, 100) + (text.length > 100 ? '...' : '')
          }
        );
      }
      
      // Reset form
      setText('');
      setShowMentions(false);
      setMentionContext(null);
      
      if (onCancel) {
        onCancel();
      }
    } catch (error) {
      console.error('Error submitting comment with mentions:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle mention selection
  const handleMentionSelect = (user) => {
    if (!textareaRef.current || !mentionContext) return;
    
    // Use enhanced insertMention function
    const currentCursorPos = textareaRef.current.selectionStart;
    const { newText, newCursorPosition } = insertMention(
      text, 
      user, 
      mentionContext.startIndex, 
      currentCursorPos
    );
    
    // Update text
    setText(newText);
    
    // Close suggestions
    setShowMentions(false);
    setMentionContext(null);
    setMentionQuery('');
    
    // Focus back on textarea and position cursor after the mention
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  return (
    <form className="fixed-comment-form" onSubmit={handleSubmit}>
      {/* User Avatar with Profile Ring */}
      {currentUser && !parentId && (
        <div className="comment-form-avatar">
          <ProfileRing ringId={currentUserRing} size="small">
            {userPhotoURL ? (
              <img src={userPhotoURL} alt={currentUser.displayName || 'User'} />
            ) : (
              <span className="comment-avatar-letter">
                {(currentUser.displayName || currentUser.email || 'U')[0].toUpperCase()}
              </span>
            )}
          </ProfileRing>
        </div>
      )}
      
      <div className="comment-input-container">
        <textarea
          ref={textareaRef}
          className={`fixed-comment-input ${text.length > 900 ? 'near-limit' : ''}`}
          value={text}
          onChange={handleInputChange}
          placeholder={parentId ? "Write a reply..." : "whats your take?"}
          rows={2}
          maxLength={1000}
          style={{
            border: `2px solid ${voteColor}`,
            outline: voteColor === 'white' ? '1px solid rgba(0,0,0,0.1)' : 'none',
            boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
            borderRadius: '24px'
          }}
        />
        {text.length > 900 && (
          <div className={`char-count ${text.length > 950 ? 'char-count-warn' : ''}`}>
            {1000 - text.length} characters left
          </div>
        )}
      </div>
      
      {showMentions && (
        <MentionSuggestions
          query={mentionQuery}
          position={mentionPosition}
          onSelect={handleMentionSelect}
          onClose={() => setShowMentions(false)}
          isVisible={showMentions}
        />
      )}
      
      <div className="comment-form-actions">
        {onCancel && !parentId && (
          <button 
            type="button" 
            className="cancel-button"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="comment-submit"
          disabled={!text.trim() || text.length > 1000 || isSubmitting}
        >
          {isSubmitting ? 'Posting...' : (parentId ? 'Reply' : 'Comment')}
        </button>
      </div>
    </form>
  );
}

export default CommentFormWithMentions;